import { useState, type JSX } from 'react';
import type {
  ClaudeUsage,
  CursorUsage,
  GrokUsage,
  OpenAIUsage,
  OpenCodeUsage,
  UsageResponse,
} from '../lib/protocol.ts';
import { isHiddenAgent } from '../lib/models.ts';
import {
  FIVE_HOUR_MS,
  WEEK_MS,
  cycleElapsedPercent,
  formatRemainingHms,
  grokWindowLabel,
  monthElapsedPercent,
  windowElapsedPercent,
} from '../lib/usage.ts';

type Meter = {
  label: string;
  percent: number;
  kind: 'month' | 'usage';
  elapsedPercent?: number | null;
};
type MiniMeter = { label: string; percent: number; elapsedPercent?: number | null };
type MiniItem =
  | { kind: 'meter'; meter: MiniMeter }
  | { kind: 'cost'; label: string; amount: string };
type CostLine = { label: string; amount: string };
type Section = {
  key: string;
  aria: string;
  meters: Meter[];
  costs: CostLine[];
  foot: string | null;
};

export function UsageCard({ usage }: { usage: UsageResponse }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const sections: Section[] = [];

  if (usage.cursor.ok) {
    sections.push({
      key: 'cursor',
      aria: 'Cursor usage',
      meters: cursorMeters(usage.cursor),
      costs: [],
      foot: cursorFoot(usage.cursor),
    });
  }

  if (usage.claude.ok) {
    sections.push({
      key: 'claude',
      aria: 'Claude Code usage',
      meters: claudeMeters(usage.claude),
      costs: [],
      foot: claudeFoot(usage.claude),
    });
  }

  if (!isHiddenAgent('grok') && usage.grok?.ok) {
    sections.push({
      key: 'grok',
      aria: 'Grok usage',
      meters: grokMeters(usage.grok),
      costs: [],
      foot: grokFoot(usage.grok),
    });
  }

  if (usage.openai?.ok) {
    sections.push({
      key: 'openai',
      aria: 'OpenAI usage',
      meters: openaiMeters(usage.openai),
      costs: openaiCosts(usage.openai),
      foot: openaiFoot(usage.openai),
    });
  }

  if (!isHiddenAgent('opencode') && usage.opencode?.ok) {
    sections.push({
      key: 'opencode',
      aria: 'OpenCode usage',
      meters: opencodeMeters(usage.opencode),
      costs: [],
      foot: opencodeFoot(usage.opencode),
    });
  }

  if (sections.length === 0) return null;

  const mini = miniItems(usage);
  const details = (
    <>
      {sections.map((section) => (
        <div key={section.key} className="usage-section" aria-label={section.aria}>
          {sections.length > 1 && <div className="usage-section-label">{sectionLabel(section.key)}</div>}
          {section.meters.map((m) => (
            <div key={m.label} className="usage-meter">
              <span className="usage-label">{m.label}</span>
              <Bar meter={m} />
              <span className="usage-pct">{m.percent}%</span>
            </div>
          ))}
          {section.costs.map((c) => (
            <div key={c.label} className="usage-cost">
              <span className="usage-label">{c.label}</span>
              <span className="usage-amount">{c.amount}</span>
            </div>
          ))}
          {section.foot && <div className="usage-foot">{section.foot}</div>}
        </div>
      ))}
    </>
  );

  if (mini.length === 0) {
    return (
      <div className="usage-card" aria-label="Plan usage">
        {details}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`usage-card${open ? '' : ' usage-collapsed'}`}
      aria-expanded={open}
      aria-label={open ? 'Plan usage, hide details' : miniAria(mini)}
      onClick={() => setOpen((v) => !v)}
    >
      {open ? (
        details
      ) : (
        <div className="usage-mini" aria-hidden="true">
          {mini.map((item) => (
            <div
              key={item.kind === 'meter' ? item.meter.label : item.label}
              className={item.kind === 'meter' ? 'usage-mini-meter' : 'usage-mini-cost'}
            >
              <span className="usage-mini-label">
                {item.kind === 'meter' ? item.meter.label : item.label}
              </span>
              {item.kind === 'meter' ? (
                <>
                  <Bar
                    meter={{
                      label: item.meter.label,
                      percent: item.meter.percent,
                      kind: 'usage',
                      elapsedPercent: item.meter.elapsedPercent,
                    }}
                  />
                  <span className="usage-pct">{item.meter.percent}%</span>
                </>
              ) : (
                <span className="usage-mini-amount">{item.amount}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </button>
  );
}

function Bar({ meter }: { meter: Meter }): JSX.Element {
  const t = meter.kind === 'month' ? 'month' : tone(meter.percent);
  const elapsed =
    meter.kind === 'usage' && meter.elapsedPercent != null
      ? Math.min(100, Math.max(0, meter.elapsedPercent))
      : null;
  return (
    <div className={t ? `usage-bar ${t}` : 'usage-bar'} aria-hidden="true">
      <div className="usage-bar-track">
        <span className="usage-bar-fill" style={{ width: `${Math.min(100, Math.max(0, meter.percent))}%` }} />
      </div>
      {elapsed !== null && <span className="usage-bar-now" style={{ left: `${elapsed}%` }} />}
    </div>
  );
}

function miniMeters(usage: UsageResponse): MiniMeter[] {
  const meters: MiniMeter[] = [];
  if (usage.cursor.ok && usage.cursor.autoPercent !== null) {
    meters.push({
      label: 'Cursor',
      percent: usage.cursor.autoPercent,
      elapsedPercent: cycleElapsedPercent(usage.cursor.startedAt, usage.cursor.resetsAt),
    });
  }
  if (usage.claude.ok) {
    const sessionFirst = usage.claude.sessionPercent !== null;
    meters.push({
      label: 'Claude',
      percent: usage.claude.sessionPercent ?? usage.claude.weekPercent ?? 0,
      elapsedPercent: sessionFirst
        ? windowElapsedPercent(usage.claude.sessionResetsAt, FIVE_HOUR_MS)
        : windowElapsedPercent(usage.claude.weekResetsAt, WEEK_MS),
    });
  }
  if (!isHiddenAgent('grok') && usage.grok?.ok && usage.grok.percent !== null) {
    meters.push({
      label: 'Grok',
      percent: usage.grok.percent,
      elapsedPercent: cycleElapsedPercent(usage.grok.startedAt, usage.grok.resetsAt),
    });
  }
  if (!isHiddenAgent('opencode') && usage.opencode?.ok) {
    const rollingFirst = usage.opencode.rollingPercent !== null;
    const weekFirst = !rollingFirst && usage.opencode.weekPercent !== null;
    meters.push({
      label: 'OpenCode',
      percent:
        usage.opencode.rollingPercent ??
        usage.opencode.weekPercent ??
        usage.opencode.monthPercent ??
        0,
      elapsedPercent: rollingFirst
        ? windowElapsedPercent(usage.opencode.rollingResetsAt, FIVE_HOUR_MS)
        : weekFirst
          ? windowElapsedPercent(usage.opencode.weekResetsAt, WEEK_MS)
          : monthElapsedPercent(usage.opencode.monthResetsAt),
    });
  }
  return meters;
}

function miniAria(items: MiniItem[]): string {
  const parts = items.map((item) =>
    item.kind === 'meter' ? `${item.meter.label} ${item.meter.percent}%` : `${item.label} ${item.amount}`,
  );
  return `Plan usage. ${parts.join('. ')}. Show details.`;
}

function miniItems(usage: UsageResponse): MiniItem[] {
  const items: MiniItem[] = miniMeters(usage).map((meter) => ({ kind: 'meter', meter }));
  if (usage.openai?.ok) {
    const percent = usage.openai.primaryPercent ?? usage.openai.secondaryPercent;
    if (percent !== null) items.push({ kind: 'meter', meter: { label: 'OpenAI', percent } });
    else if (usage.openai.usd !== null) items.push({ kind: 'cost', label: 'OpenAI', amount: formatUsd(usage.openai.usd) });
  }
  return items;
}

function sectionLabel(key: string): string {
  if (key === 'cursor') return 'Cursor';
  if (key === 'claude') return 'Claude Code';
  if (key === 'grok') return 'Grok';
  if (key === 'openai') return 'OpenAI';
  if (key === 'opencode') return 'OpenCode';
  return key;
}

function cursorMeters(usage: Extract<CursorUsage, { ok: true }>): Meter[] {
  const meters: Meter[] = [];
  const monthPercent = cycleElapsedPercent(usage.startedAt, usage.resetsAt);
  const elapsedPercent = monthPercent;
  if (monthPercent !== null) meters.push({ label: 'Month', percent: monthPercent, kind: 'month' });
  if (usage.autoPercent !== null) {
    meters.push({
      label: 'Cursor models',
      percent: usage.autoPercent,
      kind: 'usage',
      elapsedPercent,
    });
  }
  if (usage.apiPercent !== null) {
    meters.push({
      label: 'Other models',
      percent: usage.apiPercent,
      kind: 'usage',
      elapsedPercent,
    });
  }
  return meters;
}

function cursorFoot(usage: Extract<CursorUsage, { ok: true }>): string | null {
  return usage.resetsAt !== null
    ? `resets ${formatDate(usage.resetsAt)}`
    : null;
}

function claudeMeters(usage: Extract<ClaudeUsage, { ok: true }>): Meter[] {
  return [
    {
      label: fiveHourLabel(usage.sessionResetsAt),
      percent: usage.sessionPercent ?? 0,
      kind: 'usage',
      elapsedPercent: windowElapsedPercent(usage.sessionResetsAt, FIVE_HOUR_MS),
    },
    {
      label: 'Week',
      percent: usage.weekPercent ?? 0,
      kind: 'usage',
      elapsedPercent: windowElapsedPercent(usage.weekResetsAt, WEEK_MS),
    },
  ];
}

function claudeFoot(usage: Extract<ClaudeUsage, { ok: true }>): string | null {
  const parts: string[] = [];
  if (usage.sessionResetsAt !== null) {
    parts.push(`5h resets ${formatTime(usage.sessionResetsAt)}`);
  }
  if (usage.weekResetsAt !== null) {
    parts.push(`week resets ${formatDate(usage.weekResetsAt)}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function grokMeters(usage: Extract<GrokUsage, { ok: true }>): Meter[] {
  if (usage.percent === null) return [];
  return [
    {
      label: grokWindowLabel(usage),
      percent: usage.percent,
      kind: 'usage',
      elapsedPercent: cycleElapsedPercent(usage.startedAt, usage.resetsAt),
    },
  ];
}

function grokFoot(usage: Extract<GrokUsage, { ok: true }>): string | null {
  return usage.resetsAt !== null ? `resets ${formatDate(usage.resetsAt)}` : null;
}

function openaiCosts(usage: Extract<OpenAIUsage, { ok: true }>): CostLine[] {
  return usage.usd === null ? [] : [{ label: 'Transcription', amount: formatUsd(usage.usd) }];
}

function openaiFoot(usage: Extract<OpenAIUsage, { ok: true }>): string | null {
  return usage.monthStart === null ? null : `transcription since ${formatDate(usage.monthStart)}`;
}

function openaiMeters(usage: Extract<OpenAIUsage, { ok: true }>): Meter[] {
  return [
    openaiMeter('Primary', usage.primaryPercent, usage.primaryResetsAt, usage.primaryWindowSeconds),
    openaiMeter('Secondary', usage.secondaryPercent, usage.secondaryResetsAt, usage.secondaryWindowSeconds),
  ].filter((meter): meter is Meter => meter !== null);
}

function openaiMeter(label: string, percent: number | null, resetsAt: number | null, windowSeconds: number | null): Meter | null {
  if (percent === null) return null;
  const hours = windowSeconds === null ? null : windowSeconds / 3600;
  const windowLabel = hours === null ? label : hours >= 24 ? `${Math.round(hours / 24)} days` : `${Math.round(hours)} hours`;
  return { label: windowLabel, percent, kind: 'usage', elapsedPercent: windowSeconds === null ? null : windowElapsedPercent(resetsAt, windowSeconds * 1000) };
}

function opencodeMeters(usage: Extract<OpenCodeUsage, { ok: true }>): Meter[] {
  return [
    {
      label: fiveHourLabel(usage.rollingResetsAt),
      percent: usage.rollingPercent ?? 0,
      kind: 'usage',
      elapsedPercent: windowElapsedPercent(usage.rollingResetsAt, FIVE_HOUR_MS),
    },
    {
      label: 'Week',
      percent: usage.weekPercent ?? 0,
      kind: 'usage',
      elapsedPercent: windowElapsedPercent(usage.weekResetsAt, WEEK_MS),
    },
    {
      label: 'Month',
      percent: usage.monthPercent ?? 0,
      kind: 'usage',
      elapsedPercent: monthElapsedPercent(usage.monthResetsAt),
    },
  ];
}

function fiveHourLabel(resetsAt: number | null): string {
  const remaining = formatRemainingHms(resetsAt);
  return remaining ? `5 hours ${remaining}` : '5 hours';
}

function opencodeFoot(usage: Extract<OpenCodeUsage, { ok: true }>): string | null {
  const parts: string[] = [];
  if (usage.rollingResetsAt !== null) {
    parts.push(`5h resets ${formatTime(usage.rollingResetsAt)}`);
  }
  if (usage.weekResetsAt !== null) {
    parts.push(`week resets ${formatDate(usage.weekResetsAt)}`);
  }
  if (usage.monthResetsAt !== null) {
    parts.push(`month resets ${formatDate(usage.monthResetsAt)}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function formatUsd(usd: number): string {
  const digits = usd > 0 && usd < 0.01 ? 4 : 2;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(usd);
}

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function tone(percent: number): string {
  if (percent >= 95) return 'over';
  if (percent >= 80) return 'hot';
  return '';
}
